import {
  type ToolCallMessagePart,
  type ToolCallMessagePartComponent,
  type ToolCallMessagePartProps,
  useAuiState,
} from '@assistant-ui/react';
import {
  Action,
  Alert,
  Badge,
  chartColors,
  ClipboardButton,
  cn,
  Disclosure,
  Icon,
  InteractiveLineChart,
  SimpleSummary,
  toChartDate,
} from 'corso-admin-ui';
import { Mcp, mcpCodeLabel, mcpToolDisplay, toEnum } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { parseISO } from 'date-fns';
import { type ReactNode, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { groupBy } from '~/utils/map';
import { sidekickCopy } from './sidekickCopy';

const labelOf = (toolName: string) =>
  mcpToolDisplay[toEnum(toolName, Mcp.Tool)].activity;

const isRunning = ({ status }: Pick<ToolCallMessagePartProps, 'status'>) =>
  status.type === 'running';

// a tool that refused answers with isError; a call that threw arrives as assistant-ui's { error }
const toolFailure = z.union([
  Mcp.callToolResultSchema.extend({ isError: z.literal(true) }),
  z.object({ error: z.string() }),
]);

// the structured result a tool returned, for the tools the chat draws
const structuredResult = Mcp.callToolResultSchema.transform(
  ({ structuredContent }) => structuredContent,
);

// mcp_reader returns datetimes as ISO, dates as yyyy-mm-dd and 64-bit integers as numbers
const queryDate = z.union([
  z.iso.datetime({ offset: true }).transform((value) => parseISO(value)),
  z.iso.date().transform((value) => parseISO(value)),
]);

const queryCell = z.union([
  queryDate.transform((value) =>
    formatter.dateTime(value, { dateStyle: 'medium', timeStyle: 'short' }),
  ),
  // codes (In_Progress, Gift_Card) read as the Corso app's labels
  z.string().transform((value) => mcpCodeLabel[value] ?? value),
  z.number().transform((value) => formatter.count(value)),
  z
    .boolean()
    .transform((value) =>
      value ? sidekickCopy.queryResult.yes : sidekickCopy.queryResult.no,
    ),
  z.null().transform(() => '—'),
]);

// a step the model took, marked when it failed; the error text is for the model, never the merchant
function ToolActivity({ part: props }: { part: ToolCallMessagePartProps }) {
  const failed = toolFailure.safeParse(props.result).success;
  return (
    <div className="flex flex-col gap-1 text-xs">
      <p
        className={cn(
          'flex items-center gap-1 text-neutral-500',
          isRunning(props) && 'animate-pulse',
          failed && 'text-red-700',
        )}
      >
        <Icon
          icon={match({ failed, running: isRunning(props) })
            .with({ failed: true }, () => 'errorInline' as const)
            .with({ running: true }, () => 'aiInline' as const)
            .otherwise(() => 'check' as const)}
          className="size-3"
        />
        {labelOf(props.toolName)}
        {isRunning(props) ? '…' : ''}
      </p>
    </div>
  );
}

const QUERY_ROWS_SHOWN = 10;

function QueryTable({
  columns,
  rows,
}: Pick<z.infer<typeof Mcp.queryResultSchema>, 'columns' | 'rows'>) {
  const shown = rows.slice(0, QUERY_ROWS_SHOWN);
  return (
    <section className="flex w-full flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-3 text-sm">
      <h4 className="text-xs font-medium tracking-wide text-neutral-500">
        {sidekickCopy.queryResult.title}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-neutral-500">
            <tr>
              {columns.map(({ name }) => (
                <th key={name} className="px-2 py-1 font-medium">
                  {name.replaceAll('_', ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {shown.map((row, rowIndex) => (
              // oxlint-disable-next-line react/no-array-index-key -- rows have no identity
              <tr key={rowIndex}>
                {columns.map(({ name }, columnIndex) => (
                  <td key={name} className="px-2 py-1 whitespace-nowrap">
                    {queryCell.safeParse(row[columnIndex]).data}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > shown.length && (
          <p className="px-2 pt-1 text-xs text-neutral-500">
            {sidekickCopy.queryResult.more(rows.length - shown.length)}
          </p>
        )}
      </div>
    </section>
  );
}

// a template key keeps series keys apart from the datum's `date`
const seriesKey = (index: number) => `series${index}` as const;

// a line needs two points, each a date and at least one number
const MIN_CHART_POINTS = 2;
const MIN_CHART_COLUMNS = 2;

// a date first column with numeric columns after it reads best as a line chart
const chartRows = z
  .array(z.tuple([queryDate], z.number()).check(z.minLength(MIN_CHART_COLUMNS)))
  .min(MIN_CHART_POINTS);

function QueryResult(props: ToolCallMessagePartProps) {
  const [showTable, setShowTable] = useState(false);
  const result = structuredResult
    .pipe(Mcp.queryResultSchema)
    .safeParse(props.result);
  if (!result.success) return <ToolActivity part={props} />;

  const { columns, rows } = result.data;
  const chart = chartRows.safeParse(rows);
  const table = <QueryTable columns={columns} rows={rows} />;
  if (!chart.success) return table;

  const valueColumns = columns.slice(1);
  return (
    <div className="flex w-full flex-col gap-1">
      <InteractiveLineChart
        title={sidekickCopy.queryResult.title}
        data={chart.data.map(([date, ...values]) => ({
          date: toChartDate(date),
          ...Object.fromEntries(
            values.map((value, index) => [seriesKey(index), value]),
          ),
        }))}
        series={valueColumns.map(({ name }, index) => ({
          key: seriesKey(index),
          label: name.replaceAll('_', ' '),
          color: chartColors[index % chartColors.length] ?? chartColors[0],
        }))}
      />
      <Action variant="link" onClick={() => setShowTable(!showTable)}>
        <span className="text-xs">
          {showTable
            ? sidekickCopy.queryResult.hideTable
            : sidekickCopy.queryResult.showTable}
        </span>
      </Action>
      {showTable && table}
    </div>
  );
}

// the reply's last call to a tool, so a reply that called it more than once shows one card
const useLastCallId = (toolName: Mcp.Tool) =>
  useAuiState(
    ({ message }) =>
      Array.from<unknown>(message.content)
        .filter(
          (part): part is ToolCallMessagePart =>
            z
              .object({
                type: z.literal('tool-call'),
                toolName: z.literal(toolName),
              })
              .safeParse(part).success,
        )
        .at(-1)?.toolCallId,
  );

function Readability({
  contrast,
}: Pick<z.infer<typeof Mcp.portalCssResultSchema>, 'contrast'>) {
  const failing = contrast.filter(({ passes }) => !passes);
  return (
    <>
      <Alert
        variant="info"
        heading={sidekickCopy.portalCss.checksPass(
          failing.length,
          contrast.length,
        )}
      />
      {failing.map(({ pair, suggestedText }) => (
        <Alert
          key={pair}
          variant="warning"
          heading={sidekickCopy.portalCss.hardToRead(pair, suggestedText)}
        />
      ))}
      {contrast.some(({ dependsOnSite }) => dependsOnSite) && (
        <Alert heading={sidekickCopy.portalCss.dependsOnSite} />
      )}
    </>
  );
}

// status first, then what needs attention, then the actions
function PortalCard({
  heading,
  live,
  hint,
  portalUrl,
  primaryLink = false,
  css,
  children,
}: {
  heading: { title: string; status: string };
  live: boolean;
  hint?: string;
  portalUrl: string | null;
  primaryLink?: boolean;
  css?: string;
  children?: ReactNode;
}) {
  return (
    <section className="flex w-full flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium">{heading.title}</h4>
        <Badge variant={live ? 'success' : 'warning'}>{heading.status}</Badge>
      </div>
      {children}
      {(hint ?? portalUrl) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-neutral-600">{hint}</p>
          {portalUrl && (
            <Action
              icon="externalLink"
              side="right"
              variant={primaryLink ? 'primary' : 'default'}
              to={portalUrl}
              target="_blank"
              rel="noreferrer"
            >
              {sidekickCopy.portalCss.openPortal}
            </Action>
          )}
        </div>
      )}
      {css && (
        <Disclosure
          renderSummary={
            <SimpleSummary>
              <span className="text-xs text-neutral-500">
                {sidekickCopy.portalCss.viewCss}
              </span>
            </SimpleSummary>
          }
        >
          <div className="flex flex-col items-end gap-1">
            <ClipboardButton
              accessibilityLabel={sidekickCopy.portalCss.copyLabel}
              onClick={() => css}
            />
            {/* select-all: one click selects the whole block for a manual copy */}
            <pre className="max-h-80 w-full overflow-auto rounded-md bg-neutral-50 p-3 font-mono text-xs whitespace-pre-wrap select-all">
              {css}
            </pre>
          </div>
        </Disclosure>
      )}
    </section>
  );
}

function PortalCssProposal(props: ToolCallMessagePartProps) {
  const lastCallId = useLastCallId(Mcp.Tool.generatePortalCss);
  // a reply that also saved it shows the saved card instead
  const saved = useLastCallId(Mcp.Tool.savePortalCss) !== undefined;
  const result = structuredResult
    .pipe(Mcp.portalCssResultSchema)
    .safeParse(props.result);
  // no CSS yet: Sidekick is asking what to do with what's there
  if (
    !result.success ||
    result.data.needsDecision ||
    props.toolCallId !== lastCallId ||
    saved
  ) {
    return <ToolActivity part={props} />;
  }

  const { css, contrast, unsupported, portalUrl } = result.data;
  return (
    <PortalCard
      heading={sidekickCopy.portalCss.proposal}
      live={false}
      hint={sidekickCopy.portalCss.sayToSave}
      portalUrl={portalUrl}
      css={css}
    >
      <Readability contrast={contrast} />
      {unsupported.map(({ request }) => (
        <Alert
          key={request}
          heading={sidekickCopy.portalCss.unsupported(request)}
        />
      ))}
    </PortalCard>
  );
}

function PortalCssSaved(props: ToolCallMessagePartProps) {
  const result = structuredResult
    .pipe(Mcp.portalCssSaveSchema)
    .safeParse(props.result);
  if (!result.success) return <ToolActivity part={props} />;

  const { css, emailHeaderColorChanged, portalUrl } = result.data;
  return (
    <PortalCard
      heading={sidekickCopy.portalCss.saved}
      live
      portalUrl={portalUrl}
      primaryLink
      css={css}
    >
      {emailHeaderColorChanged && (
        <p className="text-neutral-700">{sidekickCopy.portalCss.emailHeader}</p>
      )}
    </PortalCard>
  );
}

function PortalCssReverted(props: ToolCallMessagePartProps) {
  const result = structuredResult
    .pipe(Mcp.portalCssRevertSchema)
    .safeParse(props.result);
  if (!result.success) return <ToolActivity part={props} />;

  const { emailHeaderColorChanged, portalUrl } = result.data;
  return (
    <PortalCard
      heading={sidekickCopy.portalCss.reverted}
      live
      portalUrl={portalUrl}
      primaryLink
    >
      {emailHeaderColorChanged && (
        <p className="text-neutral-700">{sidekickCopy.portalCss.emailHeader}</p>
      )}
    </PortalCard>
  );
}

function PortalCheckup(props: ToolCallMessagePartProps) {
  const lastCallId = useLastCallId(Mcp.Tool.auditPortalCss);
  const result = structuredResult
    .pipe(Mcp.portalCssAuditSchema)
    .safeParse(props.result);
  if (!result.success || props.toolCallId !== lastCallId) {
    return <ToolActivity part={props} />;
  }

  const { findings, storeFindings, contrast, portalUrl } = result.data;
  // one alert per problem, with every line it shows up on; what breaks the portal first
  const grouped = groupBy(
    [
      ...storeFindings.map((finding) => ({ ...finding, line: undefined })),
      ...findings,
    ],
    ({ severity, issue }) => `${severity} ${issue}`,
  );
  const problems = [...grouped.values()]
    .flatMap((group) =>
      group[0]
        ? [
            {
              severity: group[0].severity,
              issue: group[0].issue,
              lines: group.flatMap(({ line }) =>
                line === undefined ? [] : [line],
              ),
            },
          ]
        : [],
    )
    .sort(
      (a, b) =>
        Number(b.severity === Mcp.CssFindingSeverity.harmful) -
        Number(a.severity === Mcp.CssFindingSeverity.harmful),
    );
  const unreadable = contrast.filter(({ passes }) => !passes).length;
  return (
    <PortalCard
      heading={sidekickCopy.portalCss.checkup(problems.length + unreadable)}
      live={problems.length + unreadable === 0}
      portalUrl={portalUrl}
    >
      {problems.map(({ severity, issue, lines }) => (
        <Alert
          key={`${severity} ${issue}`}
          variant={
            severity === Mcp.CssFindingSeverity.harmful ? 'critical' : 'warning'
          }
          heading={sidekickCopy.portalCss.finding(issue, lines)}
        />
      ))}
      {contrast.length > 0 && <Readability contrast={contrast} />}
    </PortalCard>
  );
}

function PortalStyleReview(props: ToolCallMessagePartProps) {
  const lastCallId = useLastCallId(Mcp.Tool.describePortalStyle);
  // a reply that goes on to a new look shows that instead
  const proposed = useLastCallId(Mcp.Tool.generatePortalCss) !== undefined;
  const result = structuredResult
    .pipe(Mcp.portalStyleSummarySchema)
    .safeParse(props.result);
  if (!result.success || proposed || props.toolCallId !== lastCallId) {
    return <ToolActivity part={props} />;
  }

  const { contrast, portalUrl } = result.data;
  return (
    <PortalCard
      heading={sidekickCopy.portalCss.review}
      live
      portalUrl={portalUrl}
    >
      <Readability contrast={contrast} />
    </PortalCard>
  );
}

export const sidekickToolUis = {
  by_name: {
    [Mcp.Tool.query]: QueryResult,
    [Mcp.Tool.generatePortalCss]: PortalCssProposal,
    [Mcp.Tool.savePortalCss]: PortalCssSaved,
    [Mcp.Tool.describePortalStyle]: PortalStyleReview,
    [Mcp.Tool.revertPortalCss]: PortalCssReverted,
    [Mcp.Tool.auditPortalCss]: PortalCheckup,
  } satisfies Partial<Record<Mcp.Tool, ToolCallMessagePartComponent>>,
  Fallback: (props: ToolCallMessagePartProps) => <ToolActivity part={props} />,
};
